import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";
import styled from "styled-components";
import {
  IAlertOpen,
  IMainViewContext,
  IQueryList,
  ITableData,
  ITotalProps,
  OnChangeEvent,
  PageNationInfo
} from "../StatsContainer/StatsTypes";
import MainViewContext from "../../store";
import moment from "moment";
import HandleDate from "../StatsContainer/HandleDate";
import { queryContentReducer } from "../StatsContainer/queryReducer";
import {
  downloadCsv,
  executeQuery,
  getQueryList
} from "../../../../Utils/commonAxiosCall";
import uiString from "../string.json";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Reducers } from "../../../../../types/reducers";
import { downloadFileFromServer } from "../Utils";
import AdminStatistics from "../admin-statistics/admin-statistics";
import UserStatistics from "../user-statistics/user-statistics";

interface IProps {
  isAdmin: boolean;
}

const Container = styled.div`
  display: flex;
  flex: 1;
`;

const USER_PATH_NAME = "userstatistics";

function StatisticsList({ isAdmin }: IProps) {
  const pathName = useLocation().pathname.split("/")[2];
  const { employee_id: employeeId } = useSelector(
    (state: Reducers) => state.signInReducer
  );
  const rootStore: IMainViewContext = useContext(MainViewContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customQueryList, setCustomQueryList] = useState<IQueryList[]>([]);
  const [selectedItem, setSelectedItem] = useState<number>(0);
  const [targetPage, setTargetPage] = useState<number>(1);
  const [tableData, setTableData] = useState<ITableData>();
  const [pageNationInfo, setPageNationInfo] = useState<PageNationInfo>({
    page: 0,
    offset: 10
  });
  const [employeeSelectorOpen, openEmployeeSelector] = useState<boolean>(false);
  const [queryContent, setQueryContent] = useReducer(queryContentReducer, {
    title: "",
    description: "",
    base_query: "",
    employees: [],
    departments: [],
    approval_result: undefined,
    start_date: moment().subtract(7, "days"),
    end_date: moment(),
    publish: 0
  });
  const [selectedItemContent, setSelectedItemContent] = useState<IQueryList>({
    base_query: "",
    description: "",
    index: 0,
    publish: undefined,
    readonly: 1,
    show: 0,
    title: "",
    where_date: null,
    where_department_id: null,
    where_employee_id: null,
    where_process_result: null
  });
  const [departmentSelectorOpen, openDepartmentSelector] =
    useState<boolean>(false);
  const [alertOpen, openAlertDlg] = useState<IAlertOpen>({
    open: false,
    title: "",
    msg: "",
    type: "",
    singleButton: false
  });
  const [employeeDlgKey, setEmpDlgKey] = useState(
    `emp_${new Date().getTime()}`
  );
  const [departmentDlgKey, setDepDlgKey] = useState(
    `dep_${new Date().getTime()}`
  );

  const isMounted = useRef<boolean>(false);
  const handleDate = HandleDate({ setQueryContent });

  const handleLoading = useCallback((value: boolean) => {
    setIsLoading(value);
  }, []);

  const handleCustomQueryList = useCallback((data: IQueryList[]) => {
    setCustomQueryList(data);
  }, []);

  const handleSelectedItem = useCallback((value: number) => {
    setSelectedItem(value);
  }, []);

  const handleChangeTargetPage = useCallback(
    (event: OnChangeEvent) => {
      if (tableData && tableData.totalCount) {
        const page = parseInt(event.target.value, 10);
        const lastPage = Math.ceil(
          tableData.totalCount / pageNationInfo.offset
        );
        if (page > lastPage) {
          setTargetPage(lastPage);
          return;
        }
        if (page < 1) {
          setTargetPage(1);
          return;
        }
        setTargetPage(page);
      }
    },
    [tableData, pageNationInfo.offset]
  );

  const handleTableData = useCallback((content: ITableData) => {
    setTableData(content);
  }, []);

  const handleEmployeeSelectorOpen = useCallback(
    (value: boolean, selectedIds: string[]) => {
      if (!value && selectedIds) {
        setQueryContent({ type: "employees", employees: selectedIds });
      }
      openEmployeeSelector(value);
    },
    []
  );

  const handleSelectedItemContent = useCallback((value: IQueryList) => {
    setQueryContent({ type: "title", title: value.title });
    setQueryContent({ type: "description", description: value.description });
    setSelectedItemContent(value);
  }, []);

  const refreshData = useCallback(() => {
    let params: any;
    if (!isAdmin) {
      params = true;
    }

    getQueryList(params)
      .then(response => {
        if (isMounted.current === false) {
          return;
        }

        if (response.status === 200 && response.data) {
          handleCustomQueryList(response.data);
          for (let i = 0; i < response.data.length; i++) {
            if (response.data[i].index === selectedItem) {
              handleSelectedItemContent(response.data[i]);
              setQueryContent({
                type: "title",
                title: response.data[i].title
              });
              setQueryContent({
                type: "description",
                description: response.data[i].description
              });
              setQueryContent({
                type: "base_query",
                base_query: response.data[i].base_query
              });
              setQueryContent({
                type: "publish",
                publish: response.data[i].publish
              });
              break;
            }
          }
        }
      })
      .catch(e => console.log(e));
  }, [selectedItem, handleCustomQueryList, handleSelectedItemContent, isAdmin]);

  const handleEmployeeSelectorClose = useCallback(
    (_, selectedIds: string[]) => () => {
      handleEmployeeSelectorOpen(false, selectedIds);
    },
    [handleEmployeeSelectorOpen]
  );

  const handleDepartmentSelector = useCallback(
    (value: boolean, selectedIds: string[]) => {
      if (!value && selectedIds) {
        setQueryContent({ type: "departments", departments: selectedIds });
      }
      openDepartmentSelector(value);
    },
    []
  );

  const handleDepartmentSelectorClose = useCallback(
    (_, selectedIds: string[]) => () => {
      handleDepartmentSelector(false, selectedIds);
    },
    [handleDepartmentSelector]
  );

  const handleOpenAlertDig = useCallback(content => {
    openAlertDlg(content);
  }, []);

  const generateDlgKeys = useCallback(() => {
    setEmpDlgKey(`emp_${new Date().getTime()}`);
    setDepDlgKey(`dep_${new Date().getTime()}`);
  }, []);

  const handleListItemClick = useCallback(
    value => {
      handleSelectedItem(value.index);
      setQueryContent({ type: "title", title: value.title });
      setQueryContent({ type: "description", description: value.description });
      setQueryContent({ type: "base_query", base_query: value.base_query });
      generateDlgKeys();
      setQueryContent({ type: "employees", employees: [] });
      setQueryContent({ type: "approval_result", approval_result: undefined });

      if (value.where_date) {
        setQueryContent({
          type: "start_date",
          start_date: handleDate.startDate
        });
        setQueryContent({ type: "end_date", end_date: handleDate.endDate });
      } else {
        setQueryContent({
          type: "start_date",
          start_date: ""
        });
        setQueryContent({ type: "end_date", end_date: "" });
      }

      const content = customQueryList.find(
        element => element.index === value.index
      );
      if (content) {
        handleSelectedItemContent(content);
      }
    },
    [
      handleDate,
      customQueryList,
      generateDlgKeys,
      handleSelectedItem,
      handleSelectedItemContent
    ]
  );

  const handlePageNationInfo = useCallback((value: PageNationInfo) => {
    setPageNationInfo(value);
  }, []);

  const exeQuery = useCallback(
    (download: boolean = false) => {
      if (queryContent.base_query) {
        handleLoading(true);
        const deleteIdx = queryContent.base_query
          .toLowerCase()
          .indexOf("delete ");
        const updateIdx = queryContent.base_query
          .toLowerCase()
          .indexOf("update ");
        const insertIdx = queryContent.base_query
          .toLowerCase()
          .indexOf("insert ");
        const createIdx = queryContent.base_query
          .toLowerCase()
          .indexOf("create ");
        if (
          deleteIdx !== -1 ||
          updateIdx !== -1 ||
          insertIdx !== -1 ||
          createIdx !== -1
        ) {
          handleLoading(false);
          return handleOpenAlertDig({
            open: true,
            title: uiString.dialog.sqlRulViolation,
            msg: uiString.dialog.sqlRulViolationDescription,
            type: "sqlRulViolation",
            singleButton: false
          });
        }

        if (download) {
          downloadCsv({
            index: selectedItem,
            base_query: queryContent.base_query,
            employees:
              pathName === USER_PATH_NAME
                ? [`${employeeId}`]
                : queryContent.employees,
            departments: queryContent.departments,
            approval_result: queryContent.approval_result,
            start_date: queryContent.start_date,
            end_date: queryContent.end_date,
            pagenation: null
          })
            .then(res => {
              handleLoading(false);
              downloadFileFromServer(
                res.data,
                `${moment().format("YYYY-MM-DD-hh-mm-ss")}-adminstats.csv`
              );
            })
            .catch(e => {
              handleLoading(false);
              console.log(e);
            });
        }
      }
    },
    [
      handleLoading,
      handleOpenAlertDig,
      selectedItem,
      queryContent,
      pathName,
      employeeId
    ]
  );

  const handleApprovalResult = useCallback((value: string) => {
    // 999 : 전체
    // 1 : 처리
    // 3 : 미처리
    const option = parseInt(value, 10);
    setQueryContent({
      type: "approval_result",
      approval_result: option === 1 || option === 3 ? option : undefined
    });
  }, []);

  const transferToCSV = useCallback(() => {
    exeQuery(true);
  }, [exeQuery]);

  const searchWithPagenation = useCallback(
    (pagenation: { limit?: number; offset?: number }) => {
      if (queryContent.base_query) {
        handleLoading(true);
        const deleteIdx = queryContent.base_query
          .toLowerCase()
          .indexOf("delete ");
        const updateIdx = queryContent.base_query
          .toLowerCase()
          .indexOf("update ");
        const insertIdx = queryContent.base_query
          .toLowerCase()
          .indexOf("insert ");
        const createIdx = queryContent.base_query
          .toLowerCase()
          .indexOf("create ");
        if (
          deleteIdx !== -1 ||
          updateIdx !== -1 ||
          insertIdx !== -1 ||
          createIdx !== -1
        ) {
          handleLoading(false);
          return handleOpenAlertDig({
            open: true,
            title: uiString.dialog.sqlRulViolation,
            msg: uiString.dialog.sqlRulViolationDescription,
            type: "sqlRulViolation",
            singleButton: false
          });
        }
        let departments: any[] = [];
        if (queryContent?.departments && queryContent.departments.length > 0) {
          departments = queryContent.departments;
        } else if (pathName === USER_PATH_NAME) {
          departments = [rootStore.signInReducer.department_id];
        }
        executeQuery({
          index: selectedItem,
          base_query: queryContent.base_query,
          employees:
            pathName === USER_PATH_NAME
              ? [`${employeeId}`]
              : queryContent.employees,
          departments,
          approval_result: queryContent.approval_result,
          start_date: queryContent.start_date,
          end_date: queryContent.end_date,
          pagenation
        })
          .then(response => {
            if (response.status === 200) {
              if (!response.data) {
                handleOpenAlertDig({
                  open: true,
                  title: uiString.dialog.noData,
                  msg: uiString.dialog.noDataDesc,
                  type: "noData",
                  singleButton: true
                });
                handleLoading(false);
                return;
              }
              handleLoading(false);
              handleTableData({
                rows: response.data.rows.map((data: any) => {
                  delete data["employee_id"];
                  delete data["department_id"];
                  return data;
                }),
                totalCount: response.data.totalCount[0].ascount
              });
            }
          })
          .catch(e => {
            handleLoading(false);
            console.log(e);
          });
      }
    },
    [
      handleLoading,
      handleOpenAlertDig,
      handleTableData,
      selectedItem,
      queryContent,
      pathName,
      employeeId,
      rootStore.signInReducer
    ]
  );

  const handleCountSearchtDocs = useCallback(() => {
    return {
      data: {
        totalNumber: tableData?.totalCount
      }
    };
  }, [tableData]);

  const getData = useCallback(() => {
    let params: boolean | undefined;
    if (!isAdmin) {
      params = true;
    }

    getQueryList(params)
      .then(response => {
        if (isMounted.current === false) {
          return;
        }

        if (response.status === 200 && response.data) {
          setSelectedItem(response.data[0].index);
          setQueryContent({
            type: "title",
            title: response.data[0].title
          });
          setQueryContent({
            type: "description",
            description: response.data[0].description
          });
          setQueryContent({
            type: "base_query",
            base_query: response.data[0].base_query
          });
          setQueryContent({
            type: "publish",
            publish: response.data[0].publish
          });
          handleSelectedItemContent(response.data[0]);
          handleCustomQueryList(response.data);
        }
      })
      .catch(e => console.log(e));
  }, [handleCustomQueryList, handleSelectedItemContent, isAdmin]);

  const newProps: ITotalProps = useMemo(
    () => ({
      rootStore,
      isLoading,
      handleLoading,
      customQueryList,
      handleCustomQueryList,
      selectedItem,
      handleSelectedItem,
      targetPage,
      handleChangeTargetPage,
      employeeSelectorOpen,
      handleEmployeeSelectorOpen,
      refreshData,
      queryContent,
      setQueryContent,
      handleEmployeeSelectorClose,
      departmentSelectorOpen,
      handleDepartmentSelector,
      handleDepartmentSelectorClose,
      alertOpen,
      handleOpenAlertDig,
      tableData,
      handleTableData,
      selectedItemContent,
      pageNationInfo,
      employeeDlgKey,
      departmentDlgKey,
      generateDlgKeys,
      handleListItemClick,
      handleSelectedItemContent,
      handlePageNationInfo,
      exeQuery,
      handleApprovalResult,
      transferToCSV,
      ...handleDate,
      searchWithPagenation,
      handleCountSearchtDocs
    }),
    [
      rootStore,
      isLoading,
      handleLoading,
      customQueryList,
      handleCustomQueryList,
      selectedItem,
      handleSelectedItem,
      targetPage,
      handleChangeTargetPage,
      employeeSelectorOpen,
      handleEmployeeSelectorOpen,
      refreshData,
      queryContent,
      setQueryContent,
      handleEmployeeSelectorClose,
      departmentSelectorOpen,
      handleDepartmentSelector,
      handleDepartmentSelectorClose,
      alertOpen,
      handleOpenAlertDig,
      tableData,
      handleTableData,
      selectedItemContent,
      pageNationInfo,
      employeeDlgKey,
      departmentDlgKey,
      generateDlgKeys,
      handleListItemClick,
      handleSelectedItemContent,
      handlePageNationInfo,
      exeQuery,
      handleApprovalResult,
      transferToCSV,
      handleDate,
      searchWithPagenation,
      handleCountSearchtDocs
    ]
  );

  useEffect(() => {
    if (!isMounted.current) {
      const resetSelectedItemContent = {
        base_query: "",
        description: "",
        index: 0,
        publish: undefined,
        readonly: 1,
        show: 0,
        title: "",
        where_date: null,
        where_department_id: null,
        where_employee_id: null,
        where_process_result: null
      };
      handleSelectedItemContent(resetSelectedItemContent);
      handleSelectedItem(0);
    }
  }, [isAdmin, handleSelectedItem, handleSelectedItemContent]);

  useEffect(() => {
    isMounted.current = true;
    getData();
    return () => {
      isMounted.current = false;
    };
  }, [getData]);
  return (
    <Container>
      {isAdmin ? (
        <AdminStatistics {...newProps} />
      ) : (
        <UserStatistics {...newProps} />
      )}
    </Container>
  );
}

export default StatisticsList;
